import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useApp } from "../context/AppContext";
import { LANGS } from "../lib/i18n";
import { LOGO } from "../lib/constants";

export default function Navbar() {
  const { t, lang, setLang } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const links = [
    { k: "fleet", id: "fleet" },
    { k: "destinations", id: "destinations" },
    { k: "experience", id: "experience" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[90] transition-colors duration-500 ${
        scrolled ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <img src={LOGO} alt="HRH Riviera Maya Cancun" className="h-12 w-12 object-contain" />
          <span className="hidden sm:block font-serif text-lg leading-none tracking-wide">
            HRH <span className="text-[var(--gold)]">RIVIERA MAYA</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.k}
              data-testid={`nav-${l.k}`}
              onClick={() => goSection(l.id)}
              className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[var(--gold)] transition-colors"
            >
              {t.nav[l.k]}
            </button>
          ))}
          <Link
            to="/contact"
            data-testid="nav-contact"
            className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[var(--gold)] transition-colors"
          >
            {t.nav.contact}
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 border border-white/15 px-2 py-1">
            <Globe className="w-3.5 h-3.5 text-white/50" />
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`text-[11px] tracking-widest px-1 transition-colors ${
                  lang === l.code ? "text-[var(--gold)]" : "text-white/45 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <Link
            to="/book"
            data-testid="nav-book-now"
            className="hidden md:inline-flex btn-fill border border-[var(--gold)] text-[var(--gold)] px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300"
          >
            {t.nav.book}
          </Link>

          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <button key={l.k} onClick={() => goSection(l.id)} className="text-left text-sm uppercase tracking-[0.2em] text-white/80">
              {t.nav[l.k]}
            </button>
          ))}
          <Link to="/contact" onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.2em] text-white/80">
            {t.nav.contact}
          </Link>
          <Link to="/book" onClick={() => setOpen(false)} className="btn-fill border border-[var(--gold)] text-[var(--gold)] px-5 py-3 text-xs uppercase tracking-[0.2em] text-center">
            {t.nav.book}
          </Link>
        </div>
      )}
    </header>
  );
}
